import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ContactsService } from "../services/contactsService";
import { ContactList, ContactListWithCount } from "../types/contactList";
import { UserContact } from "../types/contact";
import { useMediaQuery } from "react-responsive";
import { FiX, FiLoader, FiTrash2, FiMoreVertical, FiEdit2, FiAlertTriangle, FiMenu, FiPlus, FiUserPlus } from "react-icons/fi";
import PageHeader from "../components/common/PageHeader";
import { ToastContainer, useToast } from "../components/common/Toast";

// Styles
import "../styles/contacts.css";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const MoreIcon = FiMoreVertical as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertTriangle as React.FC<React.SVGProps<SVGSVGElement>>;
const MenuIcon = FiMenu as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const UserPlusIcon = FiUserPlus as React.FC<React.SVGProps<SVGSVGElement>>;

// Error normalization utility - extracts error info and checks for empty results
interface ApiError {
  status?: number;
  response?: { status?: number; data?: { message?: string; code?: string; details?: string } };
  message?: string;
  code?: string;
  details?: string;
}

const normalizeApiError = (e: unknown): { isEmpty: boolean; message: string } => {
  const err = e as ApiError;
  const status = err?.status ?? err?.response?.status;
  const message = (err?.message || err?.response?.data?.message || "").toLowerCase();
  const code = err?.code || err?.response?.data?.code;
  const details = (err?.details || err?.response?.data?.details || "").toLowerCase();
  
  const isEmpty =
    status === 404 ||
    status === 204 ||
    code === "PGRST116" ||
    message.includes("multiple (or no) rows returned") ||
    message.includes("no rows") ||
    details.includes("0 rows") ||
    message.includes("no contacts") ||
    message.includes("not found") ||
    message.includes("empty");

  return {
    isEmpty,
    message: err?.message || "An error occurred",
  };
};

const Contacts: React.FC = () => {
  const { user } = useAuth();
  const { toasts, removeToast, success, error: showError } = useToast();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  // State for contact list management
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [selectedList, setSelectedList] = useState<ContactList | null>(null);
  const [listLoading, setListLoading] = useState(false);

  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Delete list confirmation state
  const [listToDelete, setListToDelete] = useState<ContactList | null>(null);
  const [deletingList, setDeletingList] = useState(false);

  // Lists for sidebar
  const [lists, setLists] = useState<ContactListWithCount[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);

  // Contacts for table
  const [contacts, setContacts] = useState<UserContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);

  // Row menu state
  const [menuForContactId, setMenuForContactId] = useState<string | null>(null);

  // Create list modal
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [newListColor, setNewListColor] = useState("#7c3aed");
  const [creatingList, setCreatingList] = useState(false);

  // Create contact modal
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [createEmail, setCreateEmail] = useState("");
  const [createName, setCreateName] = useState("");
  const [creatingContact, setCreatingContact] = useState(false);

  // Add existing contacts modal
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [availableContacts, setAvailableContacts] = useState<UserContact[]>([]);
  const [availableQuery, setAvailableQuery] = useState("");
  const [searchingAvailable, setSearchingAvailable] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());
  const [addingContacts, setAddingContacts] = useState(false);

  // Edit/Delete contact modals
  const [editingContact, setEditingContact] = useState<UserContact | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editName, setEditName] = useState("");
  const [updatingContact, setUpdatingContact] = useState(false);
  const [deletingContact, setDeletingContact] = useState<UserContact | null>(null);
  const [deletingContactLoading, setDeletingContactLoading] = useState(false);

  // Load lists
  useEffect(() => {
    const loadLists = async () => {
      if (!user) return;
      setListsLoading(true);
      setListsError(null);
      try {
        const fetched = await ContactsService.getContactLists();
        setLists(fetched as ContactListWithCount[]);
        if (!selectedListId && fetched.length > 0) {
          setSelectedListId(fetched[0].id);
        }
      } catch (e: unknown) {
        const err = e as Error;
        setListsError(err?.message || "Failed to load lists");
      } finally {
        setListsLoading(false);
      }
    };
    loadLists();
  }, [user]);

  // Load contacts when selected list changes
  useEffect(() => {
    const loadContacts = async () => {
      if (!selectedListId) {
        setContacts([]);
        return;
      }
      setContactsLoading(true);
      setContactsError(null);
      try {
        const fetched = await ContactsService.getContactsInList(selectedListId);
        setContacts(fetched as UserContact[]);
      } catch (e: unknown) {
        const { isEmpty, message } = normalizeApiError(e);
        if (isEmpty) {
          setContacts([]);
          setContactsError(null);
        } else {
          setContactsError(message || "Failed to load contacts");
        }
      } finally {
        setContactsLoading(false);
      }
    };
    loadContacts();
  }, [selectedListId]);

  // Load selected list details - using useCallback to include lists in dependency
  const loadSelectedList = useCallback(async () => {
    if (!selectedListId) {
      setSelectedList(null);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const list = await ContactsService.getContactList(selectedListId);
      setSelectedList(list);
    } catch (error: unknown) {
      console.error("Failed to load selected list:", error);
      // Use fresh lists reference from closure
      const fallback = lists.find((l) => l.id === selectedListId) || null;
      setSelectedList(fallback);
    } finally {
      setListLoading(false);
    }
  }, [selectedListId, lists]);

  useEffect(() => {
    loadSelectedList();
  }, [loadSelectedList]);

  // Close sidebar on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Click-outside handler for dropdown menu
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuForContactId(null);
      }
    };
    
    if (menuForContactId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuForContactId]);

  // Escape key handler for modals and menus
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Close dropdown menu
        if (menuForContactId) {
          setMenuForContactId(null);
          return;
        }
        // Close modals in order of priority
        if (listToDelete) {
          setListToDelete(null);
          return;
        }
        if (showCreateContact) {
          setShowCreateContact(false);
          return;
        }
        if (showAddExisting) {
          setShowAddExisting(false);
          return;
        }
        if (showCreateList) {
          setShowCreateList(false);
          return;
        }
        if (editingContact) {
          setEditingContact(null);
          return;
        }
        if (deletingContact) {
          setDeletingContact(null);
          return;
        }
      }
    };
    
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuForContactId, listToDelete, showCreateContact, showAddExisting, showCreateList, editingContact, deletingContact]);

  const handleListSelect = (listId: string) => {
    if (listId !== selectedListId) {
      setSelectedList(null);
      setListLoading(true);
    }
    setSelectedListId(listId);
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const handleDeleteList = (list: ContactList) => {
    if (list.name === "All Contacts") {
      showError("Cannot Delete", "Cannot delete the default 'All Contacts' list");
      return;
    }
    setListToDelete(list);
  };

  const confirmDeleteList = async () => {
    if (!listToDelete) return;
    setDeletingList(true);
    try {
      await ContactsService.deleteContactList(listToDelete.id);
      const deletedId = listToDelete.id;
      const nextLists = lists.filter((l) => l.id !== deletedId);
      setLists(nextLists);
      if (selectedListId === deletedId) {
        if (nextLists.length > 0) {
          setSelectedListId(nextLists[0].id);
          setSelectedList(nextLists[0]);
        } else {
          setSelectedListId(null);
          setSelectedList(null);
        }
      }
      setListToDelete(null);
      success("List Deleted", "Contact list has been deleted");
    } catch (err) {
      console.error("Failed to delete list:", err);
      showError("Delete Failed", "Failed to delete list. Please try again.");
    } finally {
      setDeletingList(false);
    }
  };

  const handleCreateListSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      const payload: { name: string; description?: string; color: string } = {
        name: newListName.trim(),
        description: newListDescription.trim() || undefined,
        color: newListColor,
      };
      const created = await ContactsService.createContactList(payload);
      const fetched = await ContactsService.getContactLists();
      setLists(fetched as ContactListWithCount[]);
      setSelectedListId(created.id);
      setShowCreateList(false);
      setNewListName("");
      setNewListDescription("");
      setNewListColor("#7c3aed");
      success("List Created", `"${created.name}" has been created`);
    } catch (err: unknown) {
      const error = err as Error;
      showError("Create Failed", error?.message || "Failed to create list");
    } finally {
      setCreatingList(false);
    }
  };

  const handleCreateContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createEmail.trim()) return;
    setCreatingContact(true);
    try {
      const newContact = await ContactsService.createContact({
        email: createEmail.trim(),
        name: createName.trim() || undefined,
      });
      if (selectedList && selectedList.name !== "All Contacts") {
        await ContactsService.addContactsToList(selectedList.id, [newContact.id]);
      }
      if (selectedListId) {
        const fetched = await ContactsService.getContactsInList(selectedListId);
        setContacts(fetched as UserContact[]);
      }
      setShowCreateContact(false);
      setCreateEmail("");
      setCreateName("");
      success("Contact Created", `${newContact.email} has been added`);
    } catch (err: unknown) {
      const error = err as Error;
      showError("Create Failed", error?.message || "Failed to create contact");
    } finally {
      setCreatingContact(false);
    }
  };

  const loadAvailableContacts = async (query = "") => {
    if (!selectedListId) return;
    setSearchingAvailable(true);
    try {
      const avail = await ContactsService.searchAvailableContacts(selectedListId, query);
      setAvailableContacts(avail as UserContact[]);
    } catch {
      setAvailableContacts([]);
    } finally {
      setSearchingAvailable(false);
    }
  };

  const handleAddSelectedContacts = async () => {
    if (!selectedListId || selectedToAdd.size === 0) return;
    setAddingContacts(true);
    try {
      await ContactsService.addContactsToList(selectedListId, Array.from(selectedToAdd));
      const fetched = await ContactsService.getContactsInList(selectedListId);
      setContacts(fetched as UserContact[]);
      setShowAddExisting(false);
      setSelectedToAdd(new Set());
      setAvailableQuery("");
      success("Contacts Added", `${selectedToAdd.size} contact(s) added to list`);
    } catch (err: unknown) {
      const error = err as Error;
      showError("Add Failed", error?.message || "Failed to add contacts");
    } finally {
      setAddingContacts(false);
    }
  };

  const handleEditContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingContact || !editEmail.trim()) return;
    setUpdatingContact(true);
    try {
      await ContactsService.updateContact(editingContact.id, {
        email: editEmail.trim(),
        name: editName.trim() || undefined,
      });
      if (selectedListId) {
        const fetched = await ContactsService.getContactsInList(selectedListId);
        setContacts(fetched as UserContact[]);
      }
      setEditingContact(null);
      success("Contact Updated", "Contact has been updated");
    } catch (err: unknown) {
      const error = err as Error;
      showError("Update Failed", error?.message || "Failed to update contact");
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!deletingContact) return;
    setDeletingContactLoading(true);
    try {
      await ContactsService.deleteContact(deletingContact.id);
      if (selectedListId) {
        const fetched = await ContactsService.getContactsInList(selectedListId);
        setContacts(fetched as UserContact[]);
      }
      setDeletingContact(null);
      success("Contact Deleted", "Contact has been removed");
    } catch (err: unknown) {
      const error = err as Error;
      showError("Delete Failed", error?.message || "Failed to delete contact");
    } finally {
      setDeletingContactLoading(false);
    }
  };

  return (
    <div className="w-full px-9 py-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <PageHeader
        title="Contacts"
        subtitle="Manage your contact lists and recipients"
        action={
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setShowAddExisting(true);
                loadAvailableContacts();
              }}
              className="su-btn-secondary"
              disabled={!selectedListId || selectedList?.name === "All Contacts"}
            >
              <UserPlusIcon className="w-4 h-4 mr-2" />
              ADD EXISTING
            </button>
            <button
              onClick={() => setShowCreateContact(true)}
              className="su-btn-primary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              CREATE CONTACT
            </button>
          </div>
        }
      />

      {/* Mobile header */}
      <div className="su-contacts-mobile-header-bar">
        <button
          onClick={() => setSidebarOpen(true)}
          className="su-contacts-mobile-menu-btn"
        >
          <MenuIcon className="w-5 h-5" />
        </button>
        <span className="su-contacts-mobile-title">
          {selectedList?.name || "Contacts"}
        </span>
        <div className="w-10" />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="su-contacts-page">
        {/* Sidebar - Contact Lists */}
        <aside className={`su-contacts-sidebar ${sidebarOpen ? "fixed inset-y-0 left-0 z-30 bg-black p-4" : "hidden md:flex md:flex-col"}`}>
          <div className="su-section">
            <div className="su-contacts-sidebar-header">
              <span className="su-contacts-sidebar-title">Lists</span>
              <button
                onClick={() => setShowCreateList(true)}
                className="su-btn-secondary text-xs px-2 py-1"
              >
                <PlusIcon className="w-3 h-3" />
              </button>
            </div>

            <div className="su-contacts-list-container">
              {listsLoading && (
                <div className="su-contacts-loading">
                  <LoaderIcon className="w-4 h-4 su-contacts-loading-spinner" />
                  <span>Loading...</span>
                </div>
              )}
              {listsError && (
                <div className="su-contacts-error">{listsError}</div>
              )}
              {!listsLoading && !listsError && lists.map((list) => (
                <div
                  key={list.id}
                  onClick={() => handleListSelect(list.id)}
                  className={`su-contacts-list-card ${selectedListId === list.id ? "active" : ""}`}
                >
                  <div className="su-contacts-list-card-header">
                    <div
                      className="su-contacts-list-color"
                      style={{ backgroundColor: list.color || "#7c3aed" }}
                    />
                    <span className="su-contacts-list-name">{list.name}</span>
                  </div>
                  <div className="su-contacts-list-count">
                    {list.contact_count || 0} contacts
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="su-contacts-main">
          <div className="su-section">
            {/* List Header */}
            {selectedList && (
              <div className="su-contacts-list-header">
                <h2 className="su-contacts-list-title">{selectedList.name}</h2>
                <div className="su-contacts-list-actions">
                  {selectedList.name !== "All Contacts" && (
                    <button
                      onClick={() => handleDeleteList(selectedList)}
                      className="su-btn-secondary"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      DELETE LIST
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Contacts Table */}
            {!isMobile ? (
              <div className="su-contacts-table-wrapper">
                <table className="su-contacts-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input type="checkbox" className="su-contacts-checkbox" />
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactsLoading && (
                      <tr>
                        <td colSpan={6}>
                          <div className="su-contacts-loading">
                            <LoaderIcon className="w-4 h-4 su-contacts-loading-spinner" />
                            <span>Loading contacts...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {contactsError && (
                      <tr>
                        <td colSpan={6}>
                          <div className="su-contacts-error">{contactsError}</div>
                        </td>
                      </tr>
                    )}
                    {!contactsLoading && !contactsError && contacts.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className="su-contacts-empty">No contacts in this list</div>
                        </td>
                      </tr>
                    )}
                    {!contactsLoading && contacts.map((contact) => (
                      <tr key={contact.id}>
                        <td>
                          <input type="checkbox" className="su-contacts-checkbox" />
                        </td>
                        <td>
                          <span className="su-contacts-name">{contact.name || "No Name"}</span>
                        </td>
                        <td className="muted">{contact.email}</td>
                        <td className="muted">{contact.settings?.phone || "—"}</td>
                        <td>
                          <span className="su-contacts-type-badge">
                            {contact.settings?.contact_type || "OTHER"}
                          </span>
                        </td>
                        <td>
                          <div className="relative" ref={menuForContactId === contact.id ? menuRef : null}>
                            <button
                              className="su-contacts-actions-btn"
                              onClick={() => setMenuForContactId(menuForContactId === contact.id ? null : contact.id)}
                              aria-haspopup="true"
                              aria-expanded={menuForContactId === contact.id}
                              aria-label="Contact actions"
                            >
                              <MoreIcon className="w-4 h-4" />
                            </button>
                            {menuForContactId === contact.id && (
                              <div className="su-contacts-actions-menu" role="menu">
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setEditingContact(contact);
                                    setEditEmail(contact.email);
                                    setEditName(contact.name || "");
                                    setMenuForContactId(null);
                                  }}
                                >
                                  <EditIcon className="w-3 h-3 inline mr-2" />
                                  Edit
                                </button>
                                <button
                                  role="menuitem"
                                  className="danger"
                                  onClick={() => {
                                    setDeletingContact(contact);
                                    setMenuForContactId(null);
                                  }}
                                >
                                  <TrashIcon className="w-3 h-3 inline mr-2" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile Cards */
              <div className="su-contacts-mobile-list">
                {contactsLoading && (
                  <div className="su-contacts-loading">
                    <LoaderIcon className="w-4 h-4 su-contacts-loading-spinner" />
                    <span>Loading...</span>
                  </div>
                )}
                {contactsError && (
                  <div className="su-contacts-error">{contactsError}</div>
                )}
                {!contactsLoading && !contactsError && contacts.length === 0 && (
                  <div className="su-contacts-empty">No contacts</div>
                )}
                {!contactsLoading && contacts.map((contact) => (
                  <div key={contact.id} className="su-contacts-mobile-card">
                    <div className="su-contacts-mobile-header">
                      <div className="su-contacts-mobile-info">
                        <div className="su-contacts-mobile-name">{contact.name || "No Name"}</div>
                        <div className="su-contacts-mobile-email">{contact.email}</div>
                        <div className="su-contacts-mobile-meta">
                          {contact.settings?.phone || "—"} • {contact.settings?.contact_type || "OTHER"}
                        </div>
                      </div>
                      <div className="relative" ref={menuForContactId === contact.id ? menuRef : null}>
                        <button
                          className="su-contacts-actions-btn"
                          onClick={() => setMenuForContactId(menuForContactId === contact.id ? null : contact.id)}
                          aria-haspopup="true"
                          aria-expanded={menuForContactId === contact.id}
                          aria-label="Contact actions"
                        >
                          <MoreIcon className="w-4 h-4" />
                        </button>
                        {menuForContactId === contact.id && (
                          <div className="su-contacts-actions-menu" role="menu">
                            <button
                              role="menuitem"
                              onClick={() => {
                                setEditingContact(contact);
                                setEditEmail(contact.email);
                                setEditName(contact.name || "");
                                setMenuForContactId(null);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              role="menuitem"
                              className="danger"
                              onClick={() => {
                                setDeletingContact(contact);
                                setMenuForContactId(null);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            <div className="su-contacts-pagination">
              <button className="su-contacts-pagination-btn" disabled>Prev</button>
              <span>Showing 1–{contacts.length} of {contacts.length}</span>
              <button className="su-contacts-pagination-btn" disabled>Next</button>
            </div>
          </div>
        </main>
      </div>

      {/* Delete List Modal */}
      {listToDelete && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-list-title">
          <div className="su-contacts-modal">
            <div className="su-contacts-modal-header">
              <div className="flex items-center gap-3">
                <div className="su-contacts-delete-icon">
                  <AlertIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 id="delete-list-title" className="su-contacts-modal-title">Delete Contact List</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone.</p>
                </div>
              </div>
              <button onClick={() => setListToDelete(null)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="su-contacts-modal-body">
              <p className="text-gray-300">
                Are you sure you want to delete the list{" "}
                <span className="font-medium">&ldquo;{listToDelete.name}&rdquo;</span>?
              </p>
              <div className="su-contacts-delete-info">
                <p>
                  <strong>Note:</strong> This will only delete the list, not the contacts.
                  All contacts in this list will remain in your account.
                </p>
              </div>
            </div>
            <div className="su-contacts-modal-footer">
              <button
                onClick={() => setListToDelete(null)}
                className="su-btn-secondary"
                disabled={deletingList}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteList}
                disabled={deletingList}
                className="su-btn-primary bg-red-600 hover:bg-red-700"
              >
                {deletingList ? (
                  <>
                    <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Delete List
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Contact Modal */}
      {showCreateContact && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="create-contact-title">
          <div className="su-contacts-modal">
            <div className="su-contacts-modal-header">
              <h3 id="create-contact-title" className="su-contacts-modal-title">Create New Contact</h3>
              <button onClick={() => setShowCreateContact(false)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateContactSubmit}>
              <div className="su-contacts-modal-body">
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Email *</label>
                  <input
                    type="email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    className="su-contacts-form-input"
                    placeholder="name@example.com"
                    required
                  />
                </div>
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Name (optional)</label>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    className="su-contacts-form-input"
                    placeholder="Contact name"
                  />
                </div>
              </div>
              <div className="su-contacts-modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreateContact(false)}
                  className="su-btn-secondary"
                  disabled={creatingContact}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingContact || !createEmail.trim()}
                  className="su-btn-primary"
                >
                  {creatingContact ? (
                    <>
                      <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                      Creating...
                    </>
                  ) : (
                    "Create Contact"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Existing Contacts Modal */}
      {showAddExisting && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-contacts-title">
          <div className="su-contacts-modal wide">
            <div className="su-contacts-modal-header">
              <h3 id="add-contacts-title" className="su-contacts-modal-title">Add Contacts to List</h3>
              <button onClick={() => setShowAddExisting(false)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="su-contacts-modal-body">
              <input
                type="text"
                placeholder="Search available contacts..."
                value={availableQuery}
                onChange={(e) => {
                  setAvailableQuery(e.target.value);
                  loadAvailableContacts(e.target.value);
                }}
                className="su-contacts-form-input mb-4"
              />
              <div className="su-contacts-add-modal-body">
                {searchingAvailable ? (
                  <div className="su-contacts-loading">
                    <LoaderIcon className="w-4 h-4 su-contacts-loading-spinner" />
                    <span>Searching...</span>
                  </div>
                ) : availableContacts.length === 0 ? (
                  <div className="su-contacts-empty">No available contacts</div>
                ) : (
                  availableContacts.map((c) => (
                    <div
                      key={c.id}
                      className={`su-contacts-available-item ${selectedToAdd.has(c.id) ? "selected" : ""}`}
                      onClick={() => {
                        const next = new Set(selectedToAdd);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        setSelectedToAdd(next);
                      }}
                    >
                      <div className="font-medium text-white">{c.name || "No Name"}</div>
                      <div className="text-sm text-gray-400">{c.email}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="su-contacts-modal-footer su-contacts-add-footer">
              <span className="su-contacts-selected-count">{selectedToAdd.size} selected</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAddExisting(false)}
                  className="su-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSelectedContacts}
                  disabled={selectedToAdd.size === 0 || addingContacts}
                  className="su-btn-primary"
                >
                  {addingContacts ? (
                    <>
                      <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                      Adding...
                    </>
                  ) : (
                    "Add Selected"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateList && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="create-list-title">
          <div className="su-contacts-modal">
            <div className="su-contacts-modal-header">
              <h3 id="create-list-title" className="su-contacts-modal-title">Create New List</h3>
              <button onClick={() => setShowCreateList(false)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateListSubmit}>
              <div className="su-contacts-modal-body">
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">List Name *</label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="su-contacts-form-input"
                    placeholder="Enter list name"
                    required
                  />
                </div>
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Description (optional)</label>
                  <input
                    type="text"
                    value={newListDescription}
                    onChange={(e) => setNewListDescription(e.target.value)}
                    className="su-contacts-form-input"
                    placeholder="Enter description"
                  />
                </div>
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Color</label>
                  <div className="su-contacts-color-picker">
                    <input
                      type="color"
                      value={newListColor}
                      onChange={(e) => setNewListColor(e.target.value)}
                      className="su-contacts-color-input"
                    />
                    <span className="su-contacts-color-value">{newListColor}</span>
                  </div>
                </div>
              </div>
              <div className="su-contacts-modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreateList(false)}
                  className="su-btn-secondary"
                  disabled={creatingList}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingList || !newListName.trim()}
                  className="su-btn-primary"
                >
                  {creatingList ? (
                    <>
                      <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                      Creating...
                    </>
                  ) : (
                    "Create List"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="edit-contact-title">
          <div className="su-contacts-modal">
            <div className="su-contacts-modal-header">
              <h3 id="edit-contact-title" className="su-contacts-modal-title">Edit Contact</h3>
              <button onClick={() => setEditingContact(null)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditContact}>
              <div className="su-contacts-modal-body">
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Email *</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="su-contacts-form-input"
                    required
                  />
                </div>
                <div className="su-contacts-form-group">
                  <label className="su-contacts-form-label">Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="su-contacts-form-input"
                  />
                </div>
              </div>
              <div className="su-contacts-modal-footer">
                <button
                  type="button"
                  onClick={() => setEditingContact(null)}
                  className="su-btn-secondary"
                  disabled={updatingContact}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingContact || !editEmail.trim()}
                  className="su-btn-primary"
                >
                  {updatingContact ? (
                    <>
                      <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Contact Modal */}
      {deletingContact && (
        <div className="su-contacts-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-contact-title">
          <div className="su-contacts-modal">
            <div className="su-contacts-modal-header">
              <div className="flex items-center gap-3">
                <div className="su-contacts-delete-icon">
                  <AlertIcon className="w-5 h-5" />
                </div>
                <h3 id="delete-contact-title" className="su-contacts-modal-title">Delete Contact</h3>
              </div>
              <button onClick={() => setDeletingContact(null)} className="su-contacts-modal-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="su-contacts-modal-body">
              <p className="text-gray-300">
                Are you sure you want to delete{" "}
                <span className="font-medium text-white">
                  {deletingContact.name || deletingContact.email}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="su-contacts-modal-footer">
              <button
                onClick={() => setDeletingContact(null)}
                className="su-btn-secondary"
                disabled={deletingContactLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteContact}
                disabled={deletingContactLoading}
                className="su-btn-primary bg-red-600 hover:bg-red-700"
              >
                {deletingContactLoading ? (
                  <>
                    <LoaderIcon className="w-4 h-4 mr-2 su-contacts-loading-spinner" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Delete Contact
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;


